import React from "react";
import { Lightbulb, Target, Building2, Shield } from "lucide-react";
import { motion } from "framer-motion";

const infoData = [
  {
    icon: Lightbulb,
    title: "Visi Kami",
    content:
      "Menjadi perusahaan penyedia jasa profesional yang tumbuh dan berkembang secara berkelanjutan, berintegritas tinggi, serta menjadi mitra terpercaya bagi seluruh klien di wilayah Papua.",
  },
  {
    icon: Target,
    title: "Misi Kami",
    content:
      "Memberikan layanan terbaik melalui tenaga kerja profesional, disiplin, dan berdedikasi tinggi, yang mengutamakan kepuasan pelanggan dan kontribusi positif terhadap masyarakat.",
  },
];

const statsData = [
  { icon: Building2, value: "15+", label: "Tahun Pengalaman" },
  { icon: Shield, value: "6", label: "Dokumen Legal Inti" },
];

// 🔹 Animasi dasar
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
  }),
};

// 🔹 Komponen kartu Visi/Misi
function InfoCard({ card, index }) {
  const Icon = card.icon;
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      custom={index}
      whileHover={{ y: -5, scale: 1.02 }}
      className="relative group"
    >
      <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-green-500 to-teal-500 opacity-0 blur-xl transition duration-500 group-hover:opacity-30"></div>
      <div className="relative flex flex-col items-start h-full p-10 bg-white/90 border border-gray-200 rounded-3xl shadow-md backdrop-blur-sm hover:shadow-xl transition-all duration-500">
        <motion.div
          animate={{
            y: [0, -5, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="mb-6 flex items-center justify-center h-20 w-20 rounded-2xl bg-gradient-to-br from-green-500 to-teal-500 shadow-md group-hover:scale-110 transition-transform duration-300"
        >
          <Icon className="w-10 h-10 text-white" />
        </motion.div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">{card.title}</h3>
        <p className="text-gray-700 leading-relaxed text-justify font-light">
          {card.content}
        </p>
      </div>
    </motion.div>
  );
}

// 🔹 Komponen statistik
function StatCard({ stat, index }) {
  const Icon = stat.icon;
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      custom={index}
      whileHover={{ y: -5, scale: 1.03 }}
      className="relative group"
    >
      <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-teal-500 to-green-500 opacity-0 blur-xl transition duration-500 group-hover:opacity-30"></div>
      <div className="relative h-full rounded-3xl border border-gray-200 bg-white p-10 text-center shadow-md backdrop-blur-sm hover:shadow-xl transition-all duration-500">
        <motion.div
          animate={{ y: [0, -5, 0] }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
          className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-green-500 shadow-md group-hover:scale-110 transition-transform duration-300"
        >
          <Icon className="w-10 h-10 text-white" />
        </motion.div>
        <motion.div
          className="text-5xl font-black text-gray-900 mb-1"
          whileHover={{ scale: 1.1 }}
          transition={{ type: "spring", stiffness: 200 }}
        >
          {stat.value}
        </motion.div>
        <div className="text-sm uppercase tracking-wider text-gray-500 font-mono">
          {stat.label}
        </div>
      </div>
    </motion.div>
  );
}

// 🔹 Komponen utama
export default function CompanyOverview() {
  return (
    <section className="relative overflow-hidden bg-white py-32 px-6 md:px-16">
      {/* Background animasi lembut */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,0,0.05)_2px,transparent_2px),linear-gradient(90deg,rgba(0,128,0,0.05)_2px,transparent_2px)] bg-[size:100px_100px]"></div>
        <motion.div
          animate={{ scale: [1, 1.05, 1], opacity: [0.8, 1, 0.8] }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute left-1/3 top-1/4 h-96 w-96 bg-green-400/10 rounded-full blur-[120px]"
        ></motion.div>
        <motion.div
          animate={{ scale: [1, 1.07, 1], opacity: [0.7, 1, 0.7] }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          className="absolute bottom-1/4 right-1/3 h-96 w-96 bg-teal-400/10 rounded-full blur-[120px]"
        ></motion.div>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="relative z-10 text-center max-w-4xl mx-auto mb-20"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-200 rounded-full backdrop-blur-sm mb-6">
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="w-2 h-2 rounded-full bg-green-500"
          ></motion.div>
          <span className="text-green-600 text-sm font-mono uppercase tracking-wider">
            Profil Perusahaan
          </span>
        </div>

        <motion.h2
          whileHover={{ scale: 1.02 }}
          className="text-5xl md:text-7xl font-black tracking-tight leading-tight mb-6"
        >
          <span className="text-gray-900">Tentang </span>
          <span className="bg-gradient-to-r from-green-500 via-teal-400 to-green-600 bg-clip-text text-transparent">
            Soney Raya
          </span>
        </motion.h2>

        <div className="mx-auto h-1 w-32 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mb-8"></div>

        <p className="text-gray-700 text-lg md:text-xl font-light leading-relaxed max-w-3xl mx-auto">
          Berdiri sejak{" "}
          <span className="font-semibold text-green-600">2010</span>, kami
          merupakan perusahaan{" "}
          <span className="font-semibold text-gray-900">
            kontraktor, leveransir, dan outsourcing
          </span>{" "}
          yang berfokus di wilayah Papua. Kami berkomitmen memberikan solusi
          layanan berbasis{" "}
          <span className="text-teal-600 font-medium">profesionalisme</span>,
          <span className="text-teal-600 font-medium"> integritas</span>, dan{" "}
          <span className="text-teal-600 font-medium">tanggung jawab</span> demi
          kepuasan dan kepercayaan klien.
        </p>
      </motion.div>

      {/* Konten utama */}
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Visi & Misi */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-24">
          {infoData.map((card, i) => (
            <InfoCard key={i} card={card} index={i} />
          ))}
        </div>

        {/* Statistik */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-3xl mx-auto">
          {statsData.map((stat, i) => (
            <StatCard key={i} stat={stat} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
